import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import type { CreatorStatus } from '@/types';
import { useToast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import { canTransition } from '../utils/transitions';
import {
  applyOptimistic,
  rollback,
  type Snapshot,
} from './use-move-optimistic';

type MoveVars = {
  id: string;
  from: CreatorStatus;
  to: CreatorStatus;
  creator: import('@/types').Creator;
};

type MoveCtx = {
  qc: ReturnType<typeof useQueryClient>;
  isAdmin: boolean;
  toast: ReturnType<typeof useToast>['toast'];
};

async function onMutate(v: MoveVars, ctx: MoveCtx): Promise<Snapshot> {
  if (!canTransition(v.from, v.to, ctx.isAdmin)) {
    ctx.toast('Transicion no permitida', 'error');
    throw new Error('invalid');
  }
  await ctx.qc.cancelQueries({ queryKey: ['pipeline', v.from] });
  await ctx.qc.cancelQueries({ queryKey: ['pipeline', v.to] });
  return applyOptimistic(ctx.qc, v);
}

export function useMoveCreator() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const ctx: MoveCtx = { qc, isAdmin, toast };
  return useMutation({
    mutationFn: (v: MoveVars) =>
      pb.collection('leads').update(v.id, { status: v.to }),
    onMutate: (v) => onMutate(v, ctx),
    onError: (_e, v, snap) => {
      rollback(qc, v, snap);
      toast('Error al mover creator', 'error');
    },
    onSettled: (_d, _e, v) => {
      qc.invalidateQueries({ queryKey: ['pipeline', v.from] });
      qc.invalidateQueries({ queryKey: ['pipeline', v.to] });
    },
  });
}
